/**
 * Opens the explainer modal when a link to the explainer page is clicked,
 * instead of navigating there.
 *
 * Interception rather than an "opens a modal" button setting, for three
 * reasons: the existing Learn More buttons only need their URL changed, links
 * added later inside page content or menus work automatically, and the anchors
 * keep a real href — so no-JS, middle-click and cmd-click all still reach the
 * full page. The theme's declarative `on:click` can't do this job, because it
 * doesn't preventDefault and the browser would navigate anyway.
 *
 * One delegated listener on document, so links rendered after load (Section
 * Rendering API responses included) are covered without re-binding.
 */
const MODAL_ID = 'oc-explainer-modal';

/** Normalizes a path for comparison: no trailing slash, no case sensitivity. */
function normalizePath(path) {
  return String(path || '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

document.addEventListener('click', (event) => {
  // Let the browser handle anything that isn't a plain left click: modifier
  // keys and middle clicks are how people deliberately ask for the real page.
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('a[href]');
  if (!link) return;

  const modal = document.getElementById(MODAL_ID);
  const explainerPath = modal?.dataset.explainerPath;
  if (!modal || !explainerPath) return;

  // Compare resolved pathnames so relative, absolute and full-origin hrefs all
  // match, and so query strings or hashes don't break the comparison.
  let linkPath;
  try {
    linkPath = new URL(link.href, window.location.origin).pathname;
  } catch (error) {
    return;
  }
  if (normalizePath(linkPath) !== normalizePath(explainerPath)) return;

  // `showDialog` is the same public method the theme's own triggers use, e.g.
  // on:click="#search-modal/showDialog" in snippets/search.liquid.
  if (typeof modal.showDialog !== 'function') return;

  event.preventDefault();
  modal.showDialog();
});
